import { cn } from '../../lib/utils';
import { formatDate } from '../../lib/date-formatting';
import type { Asset, Policy } from 'korint-sdk';
import { useTranslation } from 'react-i18next';

export type AssetStatus =
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'INACTIVE'
  | 'STOPPED'
  | 'CANCELLED'
  | 'FUTURE_START'
  | 'EDITING';

const ASSET_STATUS_CLASS: Record<AssetStatus, string> = {
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  SUSPENDED: 'bg-amber-100 text-amber-800',
  INACTIVE: 'bg-slate-100 text-slate-800',
  STOPPED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-red-100 text-red-800',
  FUTURE_START: 'bg-blue-100 text-blue-800',
  EDITING: 'bg-yellow-100 text-yellow-800',
};

interface AssetCardStatusProps {
  asset: Asset;
  policy?: Policy;
  hideIfInactive?: boolean;
  /** When false, render a plain status label without the embedded start/stop dates. */
  showDates?: boolean;
}

export const getAssetStatus = (
  asset: Asset | undefined,
  policy: Policy | undefined,
): AssetStatus => {
  if (!asset || !policy) {
    return 'INACTIVE';
  }

  if (policy.suspendedAt) {
    return 'SUSPENDED';
  }

  if (asset.createdOnBranch) {
    return 'EDITING';
  }

  const now = new Date();
  const endedAtDate = policy.endedAt ? new Date(policy.endedAt) : undefined;
  const startedAtDate = asset?.startedAt ? new Date(asset.startedAt) : undefined;

  if (asset.startedAt && new Date(asset.startedAt) > now) {
    return 'FUTURE_START';
  }

  if (asset.stoppedAt && new Date(asset.stoppedAt) > now) {
    return 'ACTIVE';
  }

  if (asset.startedAt && asset.stoppedAt && new Date(asset.stoppedAt) <= now) {
    return 'STOPPED';
  }

  if (asset.startedAt && asset.cancelledAt) {
    return 'CANCELLED';
  }

  if (!asset.cancelledAt && !asset.stoppedAt) {
    if (endedAtDate && endedAtDate > now) {
      return 'ACTIVE';
    }
    if (!endedAtDate && startedAtDate && startedAtDate <= now) {
      return 'ACTIVE';
    }
  }

  return 'INACTIVE';
};

export const AssetCardStatus = ({
  asset,
  policy,
  hideIfInactive = false,
  showDates = true,
}: AssetCardStatusProps) => {
  const { t } = useTranslation();
  const status = getAssetStatus(asset, policy);

  const getLabel = () => {
    if (!showDates) {
      return t(`assetCardStatus.${status}`);
    }
    if (status === 'STOPPED' && asset?.startedAt && asset.stoppedAt) {
      return t('assetCardStatus.STOPPED_withDates', {
        start: formatDate(new Date(asset.startedAt)),
        end: formatDate(new Date(asset.stoppedAt)),
      });
    }
    if (status === 'CANCELLED' && asset?.startedAt && asset.cancelledAt) {
      return t('assetCardStatus.CANCELLED_withDates', {
        start: formatDate(new Date(asset.startedAt)),
        end: formatDate(new Date(asset.cancelledAt)),
      });
    }
    if (status === 'FUTURE_START' && asset?.startedAt) {
      return t('assetCardStatus.FUTURE_START_withDate', {
        date: formatDate(new Date(asset.startedAt)),
      });
    }
    return t(`assetCardStatus.${status}`);
  };

  if (hideIfInactive && status === 'INACTIVE') {
    return null;
  }

  return (
    <div
      className={cn([
        ASSET_STATUS_CLASS[status],
        'inline-block mx-2 px-4 py-1 rounded-full text-xs font-semibold uppercase',
      ])}
    >
      {getLabel()}
    </div>
  );
};
